/**
 * Session panel — docked chat history beside the main sidebar.
 *
 * Session list is fetched here (not via a HomePage "bridge") so opening
 * the panel does not re-render Home and cause layout flicker.
 */
import * as React from "react";
import { useSessionList } from "@/hooks/useSessionList";

const OPEN_KEY = "omni:session-panel-open";

export type SessionPanelHandlers = {
  onSelect: (id: string) => void;
  onNewChat: () => void;
};

type SessionPanelContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  setActiveSessionId: (id: string | null) => void;
  setHandlers: (handlers: SessionPanelHandlers) => void;
  recent: ReturnType<typeof useSessionList>["recent"];
  older: ReturnType<typeof useSessionList>["older"];
  loading: boolean;
  activeSessionId: string | null;
  handlers: SessionPanelHandlers;
  refetch: ReturnType<typeof useSessionList>["refetch"];
  renameSession: ReturnType<typeof useSessionList>["renameSession"];
  deleteSession: ReturnType<typeof useSessionList>["deleteSession"];
};

const SessionPanelContext = React.createContext<SessionPanelContextValue | null>(null);

const noopHandlers: SessionPanelHandlers = {
  onSelect: () => {},
  onNewChat: () => {},
};

export function SessionPanelProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpenState] = React.useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [activeSessionId, setActiveSessionId] = React.useState<string | null>(null);
  const handlersRef = React.useRef<SessionPanelHandlers>(noopHandlers);
  const [, handlerTick] = React.useState(0);

  const { recent, older, loading, refetch, renameSession, deleteSession } =
    useSessionList(activeSessionId);

  const setOpen = React.useCallback((next: boolean) => {
    setOpenState(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const setHandlers = React.useCallback((handlers: SessionPanelHandlers) => {
    handlersRef.current = handlers;
    handlerTick((n) => n + 1);
  }, []);

  const value = React.useMemo(
    (): SessionPanelContextValue => ({
      open,
      setOpen,
      setActiveSessionId,
      setHandlers,
      recent,
      older,
      loading,
      activeSessionId,
      handlers: handlersRef.current,
      refetch,
      renameSession,
      deleteSession,
    }),
    [open, setOpen, recent, older, loading, activeSessionId, handlerTick, refetch, renameSession, deleteSession],
  );

  return (
    <SessionPanelContext.Provider value={value}>{children}</SessionPanelContext.Provider>
  );
}

export function useSessionPanel() {
  const ctx = React.useContext(SessionPanelContext);
  if (!ctx) {
    throw new Error("useSessionPanel must be used within SessionPanelProvider");
  }
  return ctx;
}
